// Shared fiber-topology tree pieces — used by both FiberMapPage's "Network Structure"
// sidebar tab and, indirectly, anywhere else that needs to render the OLT→PON→Splitter→ONU
// hierarchy. Split out of FiberMapPage.js so GeoMapPanel.js can render the same tree inside
// its sidebar without a circular import (GeoMapPanel is itself imported by FiberMapPage).
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { imbsAPI } from '../../services/api';
import {
  GitBranch, Edit2, Trash2, Plus, ChevronDown, ChevronRight,
  Info, User, Server, ExternalLink, X, AlertTriangle,
} from 'lucide-react';

const IMBS_PORTAL_URL = 'https://imbs.janitornetwork.com';

export function ratioMax(ratio) {
  const n = parseInt((ratio || '1:8').split(':')[1]);
  return isNaN(n) ? 8 : n;
}

export const LEVEL_COLORS = {
  1: { bg: 'var(--cb-dbeafe)', fg: 'var(--c-1e40af)' },
  2: { bg: 'var(--cb-ede9fe)', fg: 'var(--c-5b21b6)' },
  3: { bg: 'var(--cb-fce7f3)', fg: 'var(--c-9d174d)' },
};

export function UtilBar({ used, max }) {
  const pct = max ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const color = pct >= 90 ? 'var(--c-dc2626)' : pct >= 70 ? 'var(--c-d97706)' : 'var(--c-10b981)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 80 }}>
      <div style={{ flex: 1, height: 5, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width .3s' }} />
      </div>
      <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{used}/{max}</span>
    </div>
  );
}

export function StatusDot({ online }) {
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: online ? 'var(--cb-10b981)' : 'var(--cb-9ca3af)', marginRight: 5, flexShrink: 0,
    }} />
  );
}

// ─── Customer Info Popup ──────────────────────────────────────────────────────
function Row({ label, value, ok, highlight }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
      <span style={{ color: 'var(--c-9ca3af)' }}>{label}</span>
      <span style={{
        fontWeight: 600,
        color: ok === true ? 'var(--c-16a34a)' : ok === false ? 'var(--c-dc2626)' : highlight ? 'var(--c-d97706)' : 'var(--c-111827)',
      }}>{value}</span>
    </div>
  );
}

export function CustomerPopup({ customer, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const bb = customer.broadband;
  const catv = customer.catv;
  const plan = bb?.plan;
  const pkg  = catv?.package;
  const expiry = bb?.expiryDate ? new Date(bb.expiryDate).toLocaleDateString('en-IN') : null;
  const catvExpiry = catv?.expiryDate ? new Date(catv.expiryDate).toLocaleDateString('en-IN') : null;

  return (
    <div ref={ref} style={{
      position: 'absolute', zIndex: 200, top: '100%', right: 0,
      background: 'var(--cb-ffffff)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8,
      boxShadow: '0 8px 24px rgba(0,0,0,.13)', padding: '14px 16px',
      minWidth: 240, maxWidth: 300, fontSize: '.82rem',
    }}>
      <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1rem', color: 'var(--primary)', lineHeight: 1.2 }}>
        {bb?.username || customer.name}
      </div>
      <div style={{ color: 'var(--c-374151)', fontWeight: 600, marginTop: 2 }}>{customer.name}</div>
      <div style={{ color: 'var(--c-9ca3af)', fontSize: '.76rem' }}>{customer.customerId} · {customer.mobile || '—'}</div>

      <div style={{ borderTop: '1px solid var(--cb-f3f4f6)', margin: '10px 0 8px' }} />

      {bb && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontWeight: 700, fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--c-6b7280)', marginBottom: 4 }}>Broadband</div>
          <Row label="Plan" value={plan?.displayName || plan?.name || '—'} />
          {plan?.speed && <Row label="Speed" value={`↓${plan.speed.download} / ↑${plan.speed.upload} Mbps`} />}
          <Row label="Expiry" value={expiry || '—'} highlight={!!expiry} />
          <Row label="Status" value={bb?.isActive ? 'Active' : 'Inactive'} ok={!!bb?.isActive} />
        </div>
      )}

      {catv && (customer.customerType === 'catv' || customer.customerType === 'both') && (
        <div>
          <div style={{ borderTop: '1px solid var(--cb-f3f4f6)', marginBottom: 8 }} />
          <div style={{ fontWeight: 700, fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--c-6b7280)', marginBottom: 4 }}>CableTV</div>
          <Row label="Package" value={pkg?.name || '—'} />
          <Row label="Type" value={catv.catvType || '—'} />
          <Row label="Expiry" value={catvExpiry || '—'} highlight={!!catvExpiry} />
          <Row label="Status" value={customer.status === 'active' ? 'Active' : 'Inactive'} ok={customer.status === 'active'} />
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--cb-f3f4f6)', marginTop: 8, paddingTop: 8 }}>
        <span style={{ fontSize: '.74rem', color: 'var(--c-9ca3af)' }}>
          {customer.customerType === 'both' ? 'Combo customer' : customer.customerType}
        </span>
      </div>
    </div>
  );
}

// ─── Centered detail modal shell ──────────────────────────────────────────────
export function DetailModal({ title, subtitle, onClose, children }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--cb-ffffff)', borderRadius: 12, width: '100%', maxWidth: 420, margin: '0 16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--cb-e5e7eb)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ fontWeight: 700, fontSize: '.95rem', whiteSpace: 'nowrap' }}>{title}</span>
            {subtitle && (
              <span style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                — {subtitle}
              </span>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-9ca3af)' }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: '16px 18px', display: 'grid', gap: 10 }}>{children}</div>
      </div>
    </div>
  );
}

export function DetailRows({ rows }) {
  return rows.map(([label, val, color]) => val != null && val !== '' ? (
    <div key={label} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--cb-f3f4f6)', paddingBottom: 8 }}>
      <span style={{ width: 120, flexShrink: 0, fontSize: '.78rem', color: 'var(--c-6b7280)' }}>{label}</span>
      <span style={{
        fontFamily: /Serial|PPPoE|GPON|Username/.test(label) ? 'monospace' : 'inherit',
        fontWeight: 600, fontSize: '.85rem', color: color || 'var(--c-111111)',
      }}>
        {String(val)}
      </span>
    </div>
  ) : null);
}

// ─── OLT hardware details modal (clicking an ONU label) ─────────────────────
export function OltDetailModal({ onu, customer, onClose }) {
  const serial = (onu.serialNumber || onu._serial || '').toUpperCase();
  const isOnline = (onu.status || '').toLowerCase().includes('online') || onu.online === true;
  return (
    <DetailModal title="Hardware Details" subtitle={onu.label || serial} onClose={onClose}>
      <DetailRows rows={[
        ['Label',              onu.label],
        ['Serial Number',      serial],
        ['GPON ID',            onu.id || onu.onuGponId],
        ['PON Port',           onu.port != null ? `PON ${onu.port}` : null],
        ...(onu._storedOnly ? [] : [
          ['Status',            onu.status || (isOnline ? 'Online' : 'Offline'), isOnline ? 'var(--c-15803d)' : 'var(--c-dc2626)'],
          ['Model',             onu.model],
          ['Profile',           onu.profile],
          ['Rx Power',          onu.rxPower != null ? `${onu.rxPower} dBm` : null],
          ['Tx Power',          onu.txPower != null ? `${onu.txPower} dBm` : null],
          ['Last Deregistered', onu.lastDeregister],
          ['Deregister Reason', onu.deregisterReason],
        ]),
        ['PPPoE',              customer?.broadband?.username],
      ]} />
      {onu._storedOnly && (
        <div style={{ color: 'var(--c-92400e)', fontSize: '.78rem' }}>
          Not seen on the OLT right now — stored mapping only.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        {customer?._id && (
          <Link to={`/customers/${customer._id}?tab=hardware`} onClick={onClose}
            style={{ flex: 1, textAlign: 'center', padding: '7px 0', borderRadius: 6, background: 'var(--accent)', color: 'var(--c-ffffff)', fontWeight: 600, fontSize: '.83rem', textDecoration: 'none' }}>
            Full Hardware Details
          </Link>
        )}
        <Link to={`/olt?serial=${encodeURIComponent(serial || onu.label || '')}`} onClick={onClose}
          style={{ flex: 1, textAlign: 'center', padding: '7px 0', borderRadius: 6, background: 'var(--primary)', color: 'var(--c-ffffff)', fontWeight: 600, fontSize: '.83rem', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
          <ExternalLink size={13} /> Open in OLT
        </Link>
      </div>
    </DetailModal>
  );
}

// ─── Netzur details modal (clicking a mapped customer's name) ──────────────────
export function ImbsDetailModal({ customer, onClose }) {
  const subId = customer?.imbs?.subscriberId;
  const [sub, setSub]         = useState(null);
  const [loading, setLoading] = useState(!!subId);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!subId) return;
    imbsAPI.getSubscriber(subId)
      .then(r => {
        const raw = r.data?.data;
        setSub(raw?.data || raw || null);
      })
      .catch(err => setError(err.response?.data?.message || 'Could not load Netzur details'))
      .finally(() => setLoading(false));
  }, [subId]);

  const enabled = sub && (sub.enabled === '1' || sub.enabled === true || sub.status === 'active');
  return (
    <DetailModal title="Netzur Details" subtitle={customer.broadband?.username || customer.name} onClose={onClose}>
      {!subId ? (
        <div style={{ color: 'var(--c-92400e)', fontSize: '.8rem' }}>
          This customer is not linked to an Netzur subscriber yet.
          Save the ONU mapping with a PPPoE username to auto-link, or link from the customer page.
        </div>
      ) : loading ? (
        <div style={{ color: 'var(--text-muted)' }}>Loading from Netzur…</div>
      ) : error ? (
        <div style={{ color: 'var(--c-dc2626)', fontSize: '.8rem' }}>{error}</div>
      ) : sub ? (
        <DetailRows rows={[
          ['Netzur ID',  String(sub.subscriber_id || sub.id || subId)],
          ['Username', sub.username],
          ['Name',     sub.fullname || sub.name || sub.full_name],
          ['Mobile',   sub.mobile_no || sub.mobile],
          ['Package',  sub.package?.name || sub.package_name],
          ['Expiry',   sub.expiry_date ? new Date(sub.expiry_date).toLocaleDateString('en-IN') : null, 'var(--c-d97706)'],
          ['Status',   enabled ? 'Enabled' : 'Disabled', enabled ? 'var(--c-15803d)' : 'var(--c-dc2626)'],
        ]} />
      ) : (
        <DetailRows rows={[['Netzur ID', String(subId)]]} />
      )}
      <div style={{ display: 'flex', marginTop: 4 }}>
        <a href={IMBS_PORTAL_URL} target="_blank" rel="noreferrer"
          style={{ flex: 1, textAlign: 'center', padding: '7px 0', borderRadius: 6, background: 'var(--primary)', color: 'var(--c-ffffff)', fontWeight: 600, fontSize: '.83rem', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
          <ExternalLink size={13} /> Open Netzur Portal
        </a>
      </div>
    </DetailModal>
  );
}

// ─── Tree Node Components ─────────────────────────────────────────────────────
// Styled to match the map.html reference's compact tree (thin connector lines,
// small colored status dots, single-line rows) via the .ftree-* classes injected
// by GeoMapPanel's FGM_STYLES — the only current caller of PortNode.
export function ONURow({ onu, liveMap, onEdit, onDelete }) {
  const live = liveMap[onu.onuSerial];
  const isOnline = live
    ? ((live.status || '').toLowerCase().includes('online') || live.online === true)
    : null;
  const customer = onu.customer || onu.customerId;
  const pppoe = customer?.broadband?.username;
  const [showPopup, setShowPopup] = useState(false);
  const [showHw, setShowHw] = useState(false);
  const hwOnu = live
    ? { ...live, _serial: onu.onuSerial, onuGponId: live.id || onu.onuGponId, port: live.port ?? onu.ponPort }
    : { _serial: onu.onuSerial, onuGponId: onu.onuGponId, port: onu.ponPort, _storedOnly: true };

  return (
    <div className="ftree-row" style={{ position: 'relative', flexWrap: 'nowrap', alignItems: 'flex-start' }}>
      <span className="ftree-toggle leaf" />
      <span className="ftree-dot" style={{ marginTop: 6, background: isOnline === null ? 'var(--cb-e5e7eb)' : isOnline ? 'var(--cb-2e9e5b)' : 'var(--cb-d64545)' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Line 1: identity, exactly as the OLT reports it */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          {live?.label ? (
            <>
              <span className="ftree-label">{live.label}</span>
              <span className="ftree-meta font-mono">{onu.onuSerial}</span>
            </>
          ) : (
            <span className="ftree-label font-mono">{onu.onuSerial}</span>
          )}
          {onu.splitterPort && <span className="ftree-meta">Port {onu.splitterPort}</span>}
          {live?.rxPower != null && <span className="ftree-meta">{live.rxPower} dBm</span>}
          {isOnline !== null && (
            <span className={`ftree-status ${isOnline ? 'on' : 'off'}`}>{isOnline ? 'Online' : 'Offline'}</span>
          )}
        </div>
        {/* Line 2: who it's mapped to */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
          {customer ? (
            <>
              <span className="ftree-meta font-mono" style={{ color: 'var(--primary)', fontWeight: 700 }}>{pppoe || customer.name}</span>
              {pppoe && customer.name && <span className="ftree-meta">{customer.name}</span>}
              <span className={`badge badge-${customer.broadband?.isActive ? 'active' : 'danger'}`} style={{ fontSize: '.62rem', padding: '0 5px' }}>
                {customer.broadband?.isActive ? 'active' : 'inactive'}
              </span>
            </>
          ) : (
            <span className="ftree-meta" style={{ fontStyle: 'italic' }}>no customer mapped</span>
          )}
        </div>
      </div>
      <div className="ftree-actions" style={{ flexShrink: 0 }}>
        <button title="Hardware details — PON port, model, Rx power, deregister info" className="ftree-icon-btn"
          onClick={e => { e.stopPropagation(); setShowHw(v => !v); }}>
          <Info size={12} />
        </button>
        {customer && (
          <button title="Customer info" className="ftree-icon-btn" onClick={e => { e.stopPropagation(); setShowPopup(v => !v); }}>
            <User size={12} />
          </button>
        )}
        {onEdit && (
          <button title="Edit" className="ftree-icon-btn" onClick={e => { e.stopPropagation(); onEdit(onu); }}>
            <Edit2 size={12} />
          </button>
        )}
        {onDelete && (
          <button title="Delete" className="ftree-icon-btn" onClick={e => { e.stopPropagation(); onDelete(onu.onuSerial); }}>
            <Trash2 size={12} />
          </button>
        )}
      </div>
      {showHw && <OltDetailModal onu={hwOnu} customer={customer} onClose={() => setShowHw(false)} />}
      {showPopup && <CustomerPopup customer={customer} onClose={() => setShowPopup(false)} />}
    </div>
  );
}

export function SplitterNode({ splitter, liveMap, onEditSplitter, onDeleteSplitter, onEditOnu, onDeleteOnu, onAddOnu }) {
  const [open, setOpen] = useState(true);
  const max = ratioMax(splitter.ratio);
  const used = (splitter.onus?.length || 0) + (splitter.children?.length || 0);
  const level = splitter.level || 1;
  const lc = LEVEL_COLORS[level] || LEVEL_COLORS[1];
  // Same 70%/90% fill thresholds as the map.html reference's splitterColor().
  const pct = max ? used / max : 0;
  const dotColor = pct >= 0.9 ? 'var(--c-d64545)' : pct >= 0.7 ? 'var(--c-ffb300)' : 'var(--c-90caf9)';

  return (
    <div className="ftree-children">
      <div className="ftree-row" onClick={() => setOpen(o => !o)} style={{ flexWrap: 'nowrap' }}>
        <button className="ftree-toggle" onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <span className="ftree-dot" style={{ background: dotColor, flexShrink: 0 }} />
        <GitBranch size={13} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, overflow: 'hidden' }}>
          <span className="ftree-label" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{splitter.name}</span>
          <span style={{ background: lc.bg, color: lc.fg, borderRadius: 4, padding: '0 5px', fontSize: '.64rem', fontWeight: 700, flexShrink: 0 }}>
            L{level}
          </span>
          <span className="ftree-meta" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{splitter.ratio} · {splitter.location || 'No location'}</span>
        </div>
        <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
          <UtilBar used={used} max={max} />
        </div>
        {(onAddOnu || onEditSplitter || onDeleteSplitter) && (
          <div className="ftree-actions" style={{ flexShrink: 0 }}>
            {onAddOnu && (
              <button className="ftree-icon-btn" title="Add ONU to this splitter"
                onClick={e => { e.stopPropagation(); onAddOnu(splitter._id); }}>
                <Plus size={12} />
              </button>
            )}
            {onEditSplitter && (
              <button className="ftree-icon-btn" title="Edit" onClick={e => { e.stopPropagation(); onEditSplitter(splitter); }}>
                <Edit2 size={12} />
              </button>
            )}
            {onDeleteSplitter && (
              <button className="ftree-icon-btn" title="Delete" onClick={e => { e.stopPropagation(); onDeleteSplitter(splitter); }}>
                <Trash2 size={12} />
              </button>
            )}
          </div>
        )}
      </div>
      {open && (splitter.children || []).map(c => (
        <SplitterNode key={c._id} splitter={c} liveMap={liveMap}
          onEditSplitter={onEditSplitter} onDeleteSplitter={onDeleteSplitter}
          onEditOnu={onEditOnu} onDeleteOnu={onDeleteOnu} onAddOnu={onAddOnu} />
      ))}
      {open && splitter.onus?.length > 0 && (
        <div>
          {splitter.onus.map(o => (
            <ONURow key={o.onuSerial} onu={o} liveMap={liveMap}
              onEdit={onEditOnu} onDelete={onDeleteOnu} />
          ))}
        </div>
      )}
      {open && splitter.onus?.length === 0 && (splitter.children || []).length === 0 && (
        <div className="ftree-empty">No ONUs mapped to this splitter yet</div>
      )}
    </div>
  );
}

export function countSplitterOnus(sp) {
  return (sp.onus?.length || 0) + (sp.children || []).reduce((s, c) => s + countSplitterOnus(c), 0);
}

// ─── Horizontal Org-Chart Tree ─────────────────────────────────────────────────
// PON -> splitters -> ONUs rendered left-to-right as boxes joined by elbow
// connectors (.htree-* classes injected by GeoMapPanel's FGM_STYLES). PortNode
// (below) is the diagram's root box; HSplitterNode/HOnuBox recurse rightward.
export function HOnuBox({ onu, liveMap, onEdit, onDelete }) {
  const live = liveMap[onu.onuSerial];
  const isOnline = live
    ? ((live.status || '').toLowerCase().includes('online') || live.online === true)
    : null;
  const customer = onu.customer || onu.customerId;
  const pppoe = customer?.broadband?.username;
  const [showPopup, setShowPopup] = useState(false);
  const [showHw, setShowHw] = useState(false);
  const hwOnu = live
    ? { ...live, _serial: onu.onuSerial, onuGponId: live.id || onu.onuGponId, port: live.port ?? onu.ponPort }
    : { _serial: onu.onuSerial, onuGponId: onu.onuGponId, port: onu.ponPort, _storedOnly: true };
  const dotColor = isOnline === null ? 'var(--c-e5e7eb)' : isOnline ? 'var(--c-2e9e5b)' : 'var(--c-d64545)';

  return (
    <div className="htree-box" style={{ position: 'relative' }}>
      <div className="htree-box-top">
        <span className="ftree-dot" style={{ background: dotColor }} />
        <span style={{ fontWeight: 700, fontSize: '.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {live?.label || onu.onuSerial}
        </span>
      </div>
      <div className="htree-box-meta font-mono">{onu.onuSerial}</div>
      {customer ? (
        <div className="htree-box-cust font-mono">{pppoe || customer.name}</div>
      ) : (
        <div className="htree-box-meta" style={{ fontStyle: 'italic' }}>no customer mapped</div>
      )}
      <div className="htree-box-actions">
        <button title="Hardware details" className="ftree-icon-btn" onClick={e => { e.stopPropagation(); setShowHw(v => !v); }}>
          <Info size={12} />
        </button>
        {customer && (
          <button title="Customer info" className="ftree-icon-btn" onClick={e => { e.stopPropagation(); setShowPopup(v => !v); }}>
            <User size={12} />
          </button>
        )}
        {onEdit && (
          <button title="Edit" className="ftree-icon-btn" onClick={e => { e.stopPropagation(); onEdit(onu); }}>
            <Edit2 size={12} />
          </button>
        )}
        {onDelete && (
          <button title="Delete" className="ftree-icon-btn" onClick={e => { e.stopPropagation(); onDelete(onu.onuSerial); }}>
            <Trash2 size={12} />
          </button>
        )}
        {isOnline !== null && (
          <span className={`ftree-status ${isOnline ? 'on' : 'off'}`} style={{ marginLeft: 'auto' }}>{isOnline ? 'Online' : 'Offline'}</span>
        )}
      </div>
      {showHw && <OltDetailModal onu={hwOnu} customer={customer} onClose={() => setShowHw(false)} />}
      {showPopup && <CustomerPopup customer={customer} onClose={() => setShowPopup(false)} />}
    </div>
  );
}

export function HSplitterNode({ splitter, liveMap, onEditSplitter, onDeleteSplitter, onEditOnu, onDeleteOnu, onAddOnu }) {
  // Only the first fan-out level starts expanded. .htree-node centers each parent against
  // its own children's full rendered height, so a chain of mostly-single-child splitters
  // (L1 -> L2 -> L3 -> its 6-8 ONUs) inherits that height straight down the chain and drags
  // the PON root far down the page. Collapsing L2+ by default keeps the initial view to one
  // fan-out level; deeper branches are a click away.
  const [open, setOpen] = useState((splitter.level || 1) <= 1);
  const max = ratioMax(splitter.ratio);
  const used = (splitter.onus?.length || 0) + (splitter.children?.length || 0);
  const level = splitter.level || 1;
  const lc = LEVEL_COLORS[level] || LEVEL_COLORS[1];
  const pct = max ? used / max : 0;
  const dotColor = pct >= 0.9 ? 'var(--c-d64545)' : pct >= 0.7 ? 'var(--c-ffb300)' : 'var(--c-90caf9)';
  const hasChildren = (splitter.children?.length || 0) > 0 || (splitter.onus?.length || 0) > 0;

  return (
    <div className="htree-node">
      <div className="htree-box" onClick={() => setOpen(o => !o)}>
        <div className="htree-box-top">
          {hasChildren && (open ? <ChevronDown size={13} style={{ flexShrink: 0 }} /> : <ChevronRight size={13} style={{ flexShrink: 0 }} />)}
          <span className="ftree-dot" style={{ background: dotColor }} />
          <span style={{ fontWeight: 700, fontSize: '.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {splitter.name}
          </span>
          <span style={{ background: lc.bg, color: lc.fg, borderRadius: 4, padding: '0 5px', fontSize: '.62rem', fontWeight: 700, flexShrink: 0 }}>
            L{level}
          </span>
        </div>
        <div className="htree-box-meta">{splitter.ratio} · {splitter.location || 'No location'}</div>
        <div style={{ marginTop: 4 }}><UtilBar used={used} max={max} /></div>
        {(onAddOnu || onEditSplitter || onDeleteSplitter) && (
          <div className="htree-box-actions">
            {onAddOnu && (
              <button className="ftree-icon-btn" title="Add ONU to this splitter" onClick={e => { e.stopPropagation(); onAddOnu(splitter._id); }}>
                <Plus size={12} />
              </button>
            )}
            {onEditSplitter && (
              <button className="ftree-icon-btn" title="Edit" onClick={e => { e.stopPropagation(); onEditSplitter(splitter); }}>
                <Edit2 size={12} />
              </button>
            )}
            {onDeleteSplitter && (
              <button className="ftree-icon-btn" title="Delete" onClick={e => { e.stopPropagation(); onDeleteSplitter(splitter); }}>
                <Trash2 size={12} />
              </button>
            )}
          </div>
        )}
      </div>
      {open && hasChildren && (
        <div className="htree-children">
          {(splitter.children || []).map(c => (
            <div className="htree-branch" key={c._id}>
              <HSplitterNode splitter={c} liveMap={liveMap}
                onEditSplitter={onEditSplitter} onDeleteSplitter={onDeleteSplitter}
                onEditOnu={onEditOnu} onDeleteOnu={onDeleteOnu} onAddOnu={onAddOnu} />
            </div>
          ))}
          {(splitter.onus || []).map(o => (
            <div className="htree-branch" key={o.onuSerial}>
              <HOnuBox onu={o} liveMap={liveMap} onEdit={onEditOnu} onDelete={onDeleteOnu} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Pseudo-node bucketing a PON's ONUs that have no splitter assigned, so the chart shape
// stays consistent whether or not real splitter hardware has been mapped yet.
function HUnassignedBucket({ onus, liveMap, onEditOnu, onDeleteOnu }) {
  // Collapsed by default once the list gets long — an unassigned bucket has no splitter
  // fan-out to spread ONUs across shorter branches, so a large one renders as a single very
  // tall column that (via .htree-node's align-items:center) drags the whole diagram's root
  // far down the page. A short list is harmless left open.
  const [open, setOpen] = useState(onus.length <= 10);
  return (
    <div className="htree-node">
      <div className="htree-box" onClick={() => setOpen(o => !o)} style={{ borderColor: 'var(--cb-fde68a)', background: 'var(--cb-fffbeb)' }}>
        <div className="htree-box-top">
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          <AlertTriangle size={13} style={{ color: 'var(--c-92400e)', flexShrink: 0 }} />
          <span style={{ fontWeight: 700, fontSize: '.8rem', color: 'var(--c-92400e)' }}>Unassigned</span>
        </div>
        <div className="htree-box-meta" style={{ color: 'var(--c-92400e)' }}>
          {onus.length} ONU{onus.length !== 1 ? 's' : ''} · no splitter
        </div>
      </div>
      {open && (
        <div className="htree-children">
          {onus.map(o => (
            <div className="htree-branch" key={o.onuSerial}>
              <HOnuBox onu={o} liveMap={liveMap} onEdit={onEditOnu} onDelete={onDeleteOnu} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PortNode({ port, liveMap, onEditSplitter, onDeleteSplitter, onEditOnu, onDeleteOnu, onAddOnu, onMapOnu, onDeletePort }) {
  const [open, setOpen] = useState(true);
  const totalOnus = (port.splitters || []).reduce((s, sp) => s + countSplitterOnus(sp), 0)
    + (port.unmappedOnus?.length || 0);
  const hasAny = (port.splitters || []).length > 0 || (port.unmappedOnus || []).length > 0;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--card-bg)' }}>
      <div className="htree-scroll">
        <div className="htree-node">
          <div className="htree-box" onClick={() => setOpen(o => !o)} style={{ borderColor: 'var(--cb-1565c0)', minWidth: 190 }}>
            <div className="htree-box-top">
              {hasAny && (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
              <span className="ftree-dot" style={{ background: 'var(--cb-1565c0)', width: 11, height: 11 }} />
              <Server size={14} style={{ color: 'var(--c-1565c0)', flexShrink: 0 }} />
              <span style={{ fontWeight: 700, fontSize: '.85rem' }}>PON {port.ponPort}</span>
            </div>
            <div className="htree-box-meta">
              {port.splitters?.length} splitter{port.splitters?.length !== 1 ? 's' : ''} · {totalOnus} ONU{totalOnus !== 1 ? 's' : ''} mapped
            </div>
            <div className="htree-box-actions">
              {onMapOnu && (
                <button className="ftree-icon-btn" title={`Map an ONU on PON ${port.ponPort}`}
                  onClick={e => { e.stopPropagation(); onMapOnu(port.ponPort); }}>
                  <Plus size={12} />
                </button>
              )}
              {onDeletePort && (
                <button className="ftree-icon-btn" title={`Delete PON ${port.ponPort} with all its splitters and ONU mappings`}
                  onClick={e => { e.stopPropagation(); onDeletePort(port); }}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
          {open && hasAny && (
            <div className="htree-children">
              {(port.splitters || []).map(s => (
                <div className="htree-branch" key={s._id}>
                  <HSplitterNode splitter={s} liveMap={liveMap}
                    onEditSplitter={onEditSplitter} onDeleteSplitter={onDeleteSplitter}
                    onEditOnu={onEditOnu} onDeleteOnu={onDeleteOnu} onAddOnu={onAddOnu} />
                </div>
              ))}
              {(port.unmappedOnus || []).length > 0 && (
                <div className="htree-branch">
                  <HUnassignedBucket onus={port.unmappedOnus} liveMap={liveMap} onEditOnu={onEditOnu} onDeleteOnu={onDeleteOnu} />
                </div>
              )}
            </div>
          )}
          {!hasAny && <div className="ftree-empty">No ONUs mapped on this port</div>}
        </div>
      </div>
    </div>
  );
}
